// apps/web/src/stores/whatsapp.ts
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { whatsappApi } from '@/lib/whatsapp-api'
import type {
  WaSettings,
  UpdateWaSettingsPayload,
  WaSenderRow,
  WaSenderPayload,
  WaTemplateRow,
  WaTemplateSyncResult,
  WaCreateTemplatePayload,
  WaCampaignRow,
  WaCampaignPayload,
  WaEnrollmentSummary,
  WaInboxThreadRow,
  WaThreadDetail,
  WaReplyPayload,
  WaDirectPayload,
  WaMessage,
  WaMessageStatus,
  WaThreadUpdatePayload,
  WaTagRow,
  WaAudiencePreview,
} from '@/types/whatsapp'
import { useNotificationStore } from './notifications'

export const useWhatsappStore = defineStore('whatsapp', () => {
  const settings = ref<WaSettings | null>(null)
  const senders = ref<WaSenderRow[]>([])
  const templates = ref<WaTemplateRow[]>([])
  // ALL templates (any status) for the Settings → WhatsApp template-management list — kept
  // separate from `templates` above, which several send-time pick-lists (campaign builder,
  // compose, inbox reply) rely on holding ONLY approved+supported templates.
  const allTemplates = ref<WaTemplateRow[]>([])
  const campaigns = ref<WaCampaignRow[]>([])
  const currentCampaign = ref<WaCampaignRow | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const notify = useNotificationStore()

  // --- Inbox ---
  const threads = ref<WaInboxThreadRow[]>([])
  const unmatched = ref<WaInboxThreadRow[]>([])
  const currentThread = ref<WaThreadDetail | null>(null)
  const loadingThreads = ref(false)
  const loadingThread = ref(false)
  // Sidebar nav badge — total unread MESSAGE count across whichever conversations this viewer
  // can see (mirrors the server's InboxService.unreadCount). Fetched independently of the
  // thread lists below so the badge stays live even when the user isn't on the inbox page.
  const waUnreadCount = ref(0)

  function fail(err: any, msg: string) {
    error.value = err?.response?.data?.error?.message || err?.response?.data?.message || msg
    notify.error(error.value || msg)
  }

  async function fetchSettings() {
    loading.value = true; error.value = null
    try { const r = await whatsappApi.getSettings(); settings.value = r.data; return r.data }
    catch (e) { fail(e, 'Failed to load WhatsApp settings'); return null } finally { loading.value = false }
  }

  async function updateSettings(data: UpdateWaSettingsPayload) {
    try {
      const r = await whatsappApi.updateSettings(data)
      settings.value = r.data
      notify.success('WhatsApp settings saved')
      return r.data
    } catch (e) { fail(e, 'Failed to save WhatsApp settings'); throw e }
  }

  /**
   * Clears all `cooldown` suppressions for the org (opt-out/failed are kept), freeing those
   * numbers for immediate re-messaging. Returns the number cleared so the caller can toast it.
   */
  async function clearCooldowns(): Promise<number> {
    try {
      const r = await whatsappApi.clearCooldowns()
      const cleared = r.data.cleared
      notify.success(`Cleared ${cleared} cooldown(s).`)
      return cleared
    } catch (e) { fail(e, 'Failed to clear cooldowns'); throw e }
  }

  async function fetchSenders() {
    loading.value = true; error.value = null
    try { const r = await whatsappApi.listSenders(); senders.value = r.data; return r.data }
    catch (e) { fail(e, 'Failed to load numbers'); return [] } finally { loading.value = false }
  }

  async function createSender(data: WaSenderPayload) {
    try {
      const r = await whatsappApi.createSender(data)
      senders.value = [r.data, ...senders.value]
      notify.success('Number added')
      return r.data
    } catch (e) { fail(e, 'Failed to add number'); throw e }
  }

  async function updateSender(id: string, data: WaSenderPayload) {
    try {
      const r = await whatsappApi.updateSender(id, data)
      const idx = senders.value.findIndex(s => s.id === id)
      if (idx !== -1) senders.value[idx] = r.data
      notify.success('Number updated')
      return r.data
    } catch (e) { fail(e, 'Failed to update number'); throw e }
  }

  async function deleteSender(id: string) {
    try {
      await whatsappApi.deleteSender(id)
      senders.value = senders.value.filter(s => s.id !== id)
      notify.success('Number deleted')
    } catch (e) { fail(e, 'Failed to delete number'); throw e }
  }

  async function syncTemplates(senderId: string): Promise<WaTemplateSyncResult | null> {
    try {
      const r = await whatsappApi.syncTemplates(senderId)
      notify.success(`Synced ${r.data.synced} template(s)${r.data.unsupported ? ` (${r.data.unsupported} unsupported)` : ''}`)
      return r.data
    } catch (e) { fail(e, 'Template sync failed'); return null }
  }

  async function fetchTemplates(senderId: string) {
    loading.value = true; error.value = null
    try { const r = await whatsappApi.listTemplates(senderId); templates.value = r.data; return r.data }
    catch (e) { fail(e, 'Failed to load templates'); return [] } finally { loading.value = false }
  }

  async function fetchAllTemplates(senderId: string) {
    loading.value = true; error.value = null
    try { const r = await whatsappApi.listAllTemplates(senderId); allTemplates.value = r.data; return r.data }
    catch (e) { fail(e, 'Failed to load templates'); return [] } finally { loading.value = false }
  }

  /**
   * Authors + submits a new template to Meta. Re-throws on failure (mirrors createSender/
   * createCampaign) so the builder form can stay open and show the validation/Graph error inline
   * rather than the generic toast alone. Prepends the new row to `allTemplates` (the management
   * list) so it shows up immediately without a refetch — NOT to `templates` (the approved-only
   * pick-list), since a freshly-created template is PENDING, not sendable yet.
   */
  async function createTemplate(senderId: string, data: WaCreateTemplatePayload) {
    try {
      const r = await whatsappApi.createTemplate(senderId, data)
      allTemplates.value = [r.data, ...allTemplates.value]
      notify.success('Template submitted to Meta for review')
      return r.data
    } catch (e) { fail(e, 'Failed to create template'); throw e }
  }

  // --- Campaigns ---

  async function listCampaigns() {
    loading.value = true; error.value = null
    try { const r = await whatsappApi.listCampaigns(); campaigns.value = r.data; return r.data }
    catch (e) { fail(e, 'Failed to load campaigns'); return [] } finally { loading.value = false }
  }

  async function getCampaign(id: string) {
    loading.value = true; error.value = null
    try { const r = await whatsappApi.getCampaign(id); currentCampaign.value = r.data; return r.data }
    catch (e) { fail(e, 'Failed to load campaign'); return null } finally { loading.value = false }
  }

  async function createCampaign(data: WaCampaignPayload) {
    try {
      const r = await whatsappApi.createCampaign(data)
      campaigns.value = [r.data, ...campaigns.value]
      notify.success('Campaign created')
      return r.data
    } catch (e) { fail(e, 'Failed to create campaign'); throw e }
  }

  async function updateCampaign(id: string, data: WaCampaignPayload) {
    try {
      const r = await whatsappApi.updateCampaign(id, data)
      const idx = campaigns.value.findIndex(c => c._id === id)
      if (idx !== -1) campaigns.value[idx] = r.data
      notify.success('Saved')
      return r.data
    } catch (e) { fail(e, 'Failed to save campaign'); throw e }
  }

  /**
   * Enrols the campaign's audience and (only if >=1 contact was enrolled) flips it to sending —
   * see CampaignService.send. Deliberately doesn't self-notify (unlike most of this store's
   * mutating actions): a 0-enrolled result needs a WARNING toast built from `skipReasons`, not the
   * generic success toast, so the caller (CampaignsList's Send / WaCampaignBuilderView's confirmSend)
   * builds the toast from the returned summary.
   */
  async function sendCampaign(id: string): Promise<WaEnrollmentSummary | null> {
    try {
      const r = await whatsappApi.sendCampaign(id)
      return r.data
    } catch (e) { fail(e, 'Send failed'); throw e }
  }

  async function pauseCampaign(id: string) {
    try { await whatsappApi.pauseCampaign(id); notify.success('Paused') }
    catch (e) { fail(e, 'Pause failed'); throw e }
  }

  async function resumeCampaign(id: string) {
    try { await whatsappApi.resumeCampaign(id); notify.success('Resumed') }
    catch (e) { fail(e, 'Resume failed'); throw e }
  }

  // Silent, debounce-friendly (no shared `loading`/`error`/toast) — mirrors outreach store's
  // fetchTags, since this fires on every autocomplete keystroke.
  async function fetchTags(q?: string): Promise<WaTagRow[]> {
    try { const r = await whatsappApi.listTags(q); return r.data } catch { return [] }
  }

  /**
   * Recipient-count preview for a (tag, sender) pair — same "quiet" contract as fetchTags: no
   * shared `loading`/`error`/toast, since the builder debounces this on every tag/sender change
   * and a transient failure shouldn't interrupt typing with an error toast. Returns null on
   * failure so the caller can just hide the preview rather than show stale/wrong numbers.
   */
  async function fetchAudiencePreview(tag: string, senderId: string): Promise<WaAudiencePreview | null> {
    try { const r = await whatsappApi.getAudiencePreview(tag, senderId); return r.data } catch { return null }
  }

  // --- Inbox ---

  async function listThreads(params?: { senderId?: string; unread?: boolean }) {
    loadingThreads.value = true; error.value = null
    try { const r = await whatsappApi.listThreads(params); threads.value = r.data; return r.data }
    catch (e) { fail(e, 'Failed to load inbox'); return [] } finally { loadingThreads.value = false }
  }

  async function listUnmatched() {
    loadingThreads.value = true; error.value = null
    try { const r = await whatsappApi.listUnmatched(); unmatched.value = r.data; return r.data }
    catch (e) { fail(e, 'Failed to load unmatched conversations'); return [] } finally { loadingThreads.value = false }
  }

  /**
   * Refreshes the sidebar nav badge count. Deliberately quiet on failure (no notify/error toast,
   * unlike the rest of this store's `fail` pattern) — this runs in the background from the
   * sidebar on every org/page, and a transient failure shouldn't surface a toast for a count
   * that's cosmetic in nature; it'll just retry on the next trigger (thread open, delete, socket
   * update, or nav remount).
   */
  async function fetchUnreadCount() {
    try {
      const r = await whatsappApi.getUnreadCount()
      waUnreadCount.value = r.data.count
    } catch {
      // ignore — badge just keeps its last known value
    }
  }

  /** Find a thread-list row by conversationId across BOTH lists — a conversation lives in
   *  exactly one of `threads` (matched) or `unmatched`, and the inbox binds both. */
  function findRow(conversationId: string): WaInboxThreadRow | undefined {
    return threads.value.find(t => t.conversationId === conversationId)
      ?? unmatched.value.find(t => t.conversationId === conversationId)
  }

  async function openThread(conversationId: string) {
    loadingThread.value = true; error.value = null
    try {
      const r = await whatsappApi.getThread(conversationId)
      currentThread.value = r.data
      // Opening a thread marks it read server-side — reflect that locally without a refetch,
      // in whichever list (matched or unmatched) the row lives.
      const row = findRow(conversationId)
      if (row) row.unreadCount = 0
      // The server zeroes this thread's unreadCount as a side effect of getThread — refresh the
      // badge so it doesn't keep counting messages the user just read.
      fetchUnreadCount()
      return r.data
    } catch (e) { fail(e, 'Failed to load conversation'); return null } finally { loadingThread.value = false }
  }

  async function sendReply(conversationId: string, payload: WaReplyPayload) {
    const r = await whatsappApi.reply(conversationId, payload)
    // Optimistically append — the live socket echo of this same message (deduped below in
    // applyThreadUpdate) often arrives BEFORE this response does, so guard against double-adding
    // it here too (race-safe in either arrival order).
    if (currentThread.value?.conversation.conversationId === conversationId) {
      const dup = currentThread.value.messages.some(
        m => m._id === r.data._id || (r.data.waMessageId && m.waMessageId === r.data.waMessageId),
      )
      if (!dup) currentThread.value.messages.push(r.data)
    }
    const row = findRow(conversationId)
    if (row) {
      row.lastMessageSnippet = r.data.body
      row.lastOutboundAt = r.data.createdAt
    }
    return r.data
  }

  async function convertToDeal(conversationId: string) {
    try {
      const r = await whatsappApi.convertToDeal(conversationId)
      const dealId = r.data._id
      const row = findRow(conversationId)
      if (row) row.dealId = dealId
      if (currentThread.value?.conversation.conversationId === conversationId) {
        currentThread.value.conversation.dealId = dealId
      }
      notify.success('Deal created')
      return r.data
    } catch (e) { fail(e, 'Failed to convert to deal'); throw e }
  }

  /**
   * Permanent hard-delete of a conversation (+ its messages/enrollments) server-side. Removes it
   * from BOTH `threads` and `unmatched` locally (a conversation only ever lives in one, but the
   * caller shouldn't need to know which) and clears `currentThread` if it was the open one.
   */
  async function deleteConversation(conversationId: string) {
    try {
      await whatsappApi.deleteConversation(conversationId)
      threads.value = threads.value.filter(t => t.conversationId !== conversationId)
      unmatched.value = unmatched.value.filter(t => t.conversationId !== conversationId)
      if (currentThread.value?.conversation.conversationId === conversationId) {
        currentThread.value = null
      }
      notify.success('Conversation deleted')
      // A deleted conversation could have carried unread messages — drop them from the badge.
      fetchUnreadCount()
    } catch (e) { fail(e, 'Failed to delete conversation'); throw e }
  }

  // --- Direct (one-off send to a contact, not tied to an existing conversation) ---

  /**
   * One-off "Compose WhatsApp" send. Deliberately thin (no store-side notify/catch, mirroring
   * sendReply above) — ComposeWhatsappModal needs to distinguish a 409 (window closed, switch to
   * template mode) from every other failure (show a generic error toast), so the try/catch lives
   * at the call site, not here.
   */
  async function sendDirect(contactId: string, payload: WaDirectPayload): Promise<WaMessage> {
    const r = await whatsappApi.directSend(contactId, payload)
    return r.data
  }

  /**
   * Apply a live `wa-thread-update` socket payload: appends a new message to the open thread
   * (deduped by _id, since the sender's own reply also gets echoed back over the socket) or
   * patches an existing message's delivery/read status by waMessageId. Always refreshes the
   * matching thread-list row's snippet/unread-count/order, whether or not that thread is open.
   */
  function applyThreadUpdate(payload: WaThreadUpdatePayload) {
    const { conversationId, message, statusPatch, mediaPatch } = payload
    const isOpen = currentThread.value?.conversation.conversationId === conversationId

    if (isOpen && currentThread.value) {
      if (message) {
        const exists = currentThread.value.messages.some(
          m => m._id === message._id || (message.waMessageId && m.waMessageId === message.waMessageId),
        )
        if (!exists) {
          currentThread.value.messages.push({
            _id: message._id,
            conversationId: message.conversationId,
            direction: message.direction,
            type: message.type,
            body: message.body,
            status: 'sent' as WaMessageStatus,
            waMessageId: message.waMessageId,
            createdAt: message.createdAt,
            mediaStatus: message.mediaStatus,
            mediaFilename: message.mediaFilename,
          })
        }
        // A new INBOUND message reopens the 24h customer-service window server-side
        // (ConversationService.openWindow sets windowExpiresAt = now + 24h unconditionally).
        // Mirror that deterministically so the free-form composer becomes available at once,
        // instead of staying "window closed" until the thread is reloaded.
        if (message.direction === 'inbound') {
          currentThread.value.conversation.windowExpiresAt =
            new Date(new Date(message.createdAt).getTime() + 24 * 60 * 60 * 1000).toISOString()
        }
      } else if (statusPatch) {
        const m = currentThread.value.messages.find(mm => mm.waMessageId === statusPatch.waMessageId)
        if (m) m.status = statusPatch.status as WaMessageStatus
      } else if (mediaPatch) {
        // An inbound media message's async download resolving (MediaDownloadService) — swap the
        // "downloading…" placeholder for the real attachment, or show "media unavailable" on failure.
        const m = currentThread.value.messages.find(mm => mm._id === mediaPatch.messageId)
        if (m) {
          m.mediaStatus = mediaPatch.mediaStatus
          m.attachmentId = mediaPatch.attachmentId
          m.mediaMimeType = mediaPatch.mediaMimeType
          m.mediaFilename = mediaPatch.mediaFilename
        }
      }
    }

    if (!message) return
    // Patch whichever list (matched or unmatched) holds this conversation — the Unmatched
    // tab binds `unmatched.value` and must stay live too.
    const list = threads.value.some(t => t.conversationId === conversationId) ? threads.value
      : unmatched.value.some(t => t.conversationId === conversationId) ? unmatched.value
      : null
    if (!list) return
    const row = list.find(t => t.conversationId === conversationId)!
    row.lastMessageSnippet = message.body
    if (message.direction === 'inbound') {
      row.lastInboundAt = message.createdAt
      if (!isOpen) row.unreadCount = (row.unreadCount || 0) + 1
    } else {
      row.lastOutboundAt = message.createdAt
    }
    // Bump to the top of its list — mirrors the server's newest-first sort.
    const idx = list.indexOf(row)
    if (idx > 0) {
      list.splice(idx, 1)
      list.unshift(row)
    }
  }

  return {
    settings, senders, templates, allTemplates, campaigns, currentCampaign, loading, error,
    fetchSettings, updateSettings, clearCooldowns,
    fetchSenders, createSender, updateSender, deleteSender,
    syncTemplates, fetchTemplates, fetchAllTemplates, createTemplate,
    listCampaigns, getCampaign, createCampaign, updateCampaign,
    sendCampaign, pauseCampaign, resumeCampaign, fetchTags, fetchAudiencePreview,
    // Inbox
    threads, unmatched, currentThread, loadingThreads, loadingThread, waUnreadCount,
    listThreads, listUnmatched, fetchUnreadCount, openThread, sendReply, convertToDeal, deleteConversation, applyThreadUpdate,
    sendDirect,
  }
})
